import React, { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { firebaseAuth } from '../firebase';
import { theme } from '../theme';

export default function LoginPage({ onRegister }) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [info, setInfo]         = useState('');
  const [busy, setBusy]         = useState(false);
  const [showReset, setShowReset] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    setError(''); setInfo('');
    setBusy(true);
    try {
      await signInWithEmailAndPassword(firebaseAuth, email.trim(), password);
      // onAuthStateChanged in AuthContext will pick up the new user automatically
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setBusy(false);
    }
  }

  async function handleReset(e) {
    e.preventDefault();
    setError(''); setInfo('');
    if (!email.trim()) { setError('Enter your username above first.'); return; }
    setBusy(true);
    try {
      await sendPasswordResetEmail(firebaseAuth, email.trim());
      setInfo('Password reset email sent — check your inbox.');
      setShowReset(false);
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title="Sign in to FusionQuery">
      <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <AuthInput label="Username (email)" type="email" value={email}
          onChange={e => setEmail(e.target.value)} autoFocus placeholder="you@example.com" />
        <AuthInput label="Password" type="password" value={password}
          onChange={e => setPassword(e.target.value)} />

        {error && <p style={{ color: theme.danger, fontSize: 13 }}>{error}</p>}
        {info  && <p style={{ color: theme.success, fontSize: 13 }}>{info}</p>}

        <AuthButton disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</AuthButton>
      </form>

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
        <button onClick={() => setShowReset(s => !s)} style={linkStyle}>
          {showReset ? 'Cancel reset' : 'Forgot password?'}
        </button>
        {showReset && (
          <button onClick={handleReset} disabled={busy} style={linkStyle}>
            Send reset email to {email || '…'}
          </button>
        )}
        <span style={{ color: theme.textMuted, fontSize: 13 }}>
          No account?{' '}
          <button onClick={onRegister} style={linkStyle}>Create one</button>
        </span>
      </div>
    </AuthShell>
  );
}

// ── Shared layout components ─────────────────────────────────────────────────

export function AuthShell({ title, children }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: theme.bg, padding: '24px 16px',
    }}>
      <div style={{
        width: '100%', maxWidth: 400,
        background: theme.surface, border: `1px solid ${theme.border}`,
        borderRadius: 12, padding: '32px 28px',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <img src="/logo.png" alt="" style={{ height: 32, width: 'auto' }} />
          <span style={{ fontWeight: 700, fontSize: 16, color: theme.text }}>FusionQuery</span>
        </div>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: theme.text, marginBottom: 20 }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

export function AuthInput({ label, ...props }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 13, color: theme.textMuted }}>{label}</span>
      <input
        {...props}
        required
        style={{
          background: theme.bg, border: `1px solid ${theme.border}`,
          borderRadius: 6, padding: '8px 10px', color: theme.text,
          fontSize: 14, outline: 'none',
          transition: 'border-color 0.15s',
        }}
        onFocus={e => e.target.style.borderColor = theme.borderFocus}
        onBlur={e  => e.target.style.borderColor = theme.border}
      />
    </label>
  );
}

export function AuthButton({ children, ...props }) {
  return (
    <button
      type="submit"
      {...props}
      style={{
        marginTop: 4,
        background: theme.accent, color: '#fff', border: 'none',
        borderRadius: 6, padding: '9px 16px', fontSize: 14, fontWeight: 600,
        cursor: props.disabled ? 'not-allowed' : 'pointer',
        opacity: props.disabled ? 0.6 : 1,
        transition: 'background 0.15s, opacity 0.15s',
      }}
    >
      {children}
    </button>
  );
}

const linkStyle = {
  background: 'none', border: 'none', color: theme.accent,
  cursor: 'pointer', fontSize: 13, textDecoration: 'underline', padding: 0,
};

// ── Firebase error codes → human messages ───────────────────────────────────
function friendlyError(code) {
  switch (code) {
    case 'auth/invalid-email':           return 'Invalid username (must be a valid email address).';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':      return 'Incorrect username or password.';
    case 'auth/too-many-requests':       return 'Too many attempts. Try again later.';
    case 'auth/email-already-in-use':    return 'An account with this username already exists.';
    case 'auth/weak-password':           return 'Password must be at least 6 characters.';
    case 'auth/user-disabled':           return 'This account has been disabled.';
    case 'auth/network-request-failed':  return 'Network error — check your connection.';
    default:                             return 'Something went wrong. Please try again.';
  }
}
